<template>
  <div v-if="visible" class="excel-viewer-overlay">
    <div
      class="excel-viewer"
      :class="{ dragging: isDragging }"
      ref="viewer"
      :style="viewerStyle"
    >
      <div class="excel-header" @mousedown.stop="startDrag">
        <span class="exceltitle">{{ title }}</span>
        <div class="excel-controls">
          <button @click="zoomOut">−</button>
          <button @click="zoomIn">+</button>
          <button @click="toggleFitMode" :title="fitModeTitle">
            {{ fitModeSymbol }}
          </button>
          <button @click="toggleFullscreen" class="buttonexpand">⛶</button>
          <button @click="closeModal" class="buttonclose">✖</button>
        </div>
      </div>

      <div class="table-container" ref="tableContainer">
        <div :style="tableStyle">
          <table border="1" cellspacing="0" cellpadding="5">
            <tbody>
              <tr v-for="(row, rIndex) in rows" :key="rIndex">
                <td v-for="cIndex in maxColumns" :key="cIndex">
                  {{ row[cIndex - 1] !== undefined ? row[cIndex - 1] : "" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-for="dir in directions"
        :key="dir"
        :class="['resize-handle', dir]"
        @mousedown.prevent="startResize(dir, $event)"
      ></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ExcelViewer",
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    visible: {
      type: Boolean,
      required: true,
    },
    title: { type: String, default: "File Preview" },
  },
  data() {
    return {
      isDragging: false,
      isResizing: false,
      dragOffset: { x: 0, y: 0 },
      resizeDir: "",
      startPos: { x: 0, y: 0 },
      startSize: { width: 0, height: 0 },
      startCoords: { top: 0, left: 0 },
      position: { top: 50, left: 50 },
      size: { width: 800, height: 600 },
      directions: [
        "top",
        "right",
        "bottom",
        "left",
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
      ],
      isFullscreen: false,
      initialSize: { width: 800, height: 600 },
      initialPosition: { top: 50, left: 50 },
      zoomLevel: 1,
      fitMode: "", // "width", "page", ""
    };
  },
  computed: {
    maxColumns() {
      if (!this.rows.length) return 0;
      return this.rows.reduce((max, row) => Math.max(max, row.length), 0);
    },
    viewerStyle() {
      return {
        top: this.position.top + "px",
        left: this.position.left + "px",
        width: this.size.width + "px",
        height: this.size.height + "px",
      };
    },
    tableStyle() {
      return {
        transform: `scale(${this.zoomLevel})`,
        transformOrigin: "top left",
        width: this.zoomLevel !== 1 ? "max-content" : "100%",
      };
    },
    fitModeSymbol() {
      if (this.fitMode === "width") return "⇲"; // Adatta alla pagina
      if (this.fitMode === "page") return "↔"; // Adatta alla larghezza
      return "↔"; // Default
    },
    fitModeTitle() {
      if (this.fitMode === "width") return "Adatta alla pagina";
      if (this.fitMode === "page") return "Adatta alla larghezza";
      return "Adatta alla larghezza";
    },
  },
  watch: {
    size: {
      handler() {
        if (this.fitMode) this.updateFitZoom();
      },
      deep: true,
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    startDrag(event) {
      this.isDragging = true;
      this.dragOffset = {
        x: event.clientX - this.position.left,
        y: event.clientY - this.position.top,
      };
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      this.position.left = event.clientX - this.dragOffset.x;
      this.position.top = event.clientY - this.dragOffset.y;
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },

    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.initialSize = { ...this.size };
        this.initialPosition = { ...this.position };
        this.size = {
          width: window.innerWidth * 0.9,
          height: window.innerHeight * 0.9,
        };
        this.position = {
          top: window.innerHeight * 0.05,
          left: window.innerWidth * 0.05,
        };
      } else {
        this.size = { ...this.initialSize };
        this.position = { ...this.initialPosition };
      }
      this.isFullscreen = !this.isFullscreen;
    },
    toggleFitMode() {
      if (this.fitMode === "width") {
        this.fitMode = "page";
      } else if (this.fitMode === "page") {
        this.fitMode = "width";
      } else {
        this.fitMode = "width";
      }
      this.updateFitZoom();
    },

    startResize(dir, event) {
      this.isResizing = true;
      this.resizeDir = dir;
      this.startPos = { x: event.clientX, y: event.clientY };
      this.startSize = { ...this.size };
      this.startCoords = { ...this.position };
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
    },
    onResize(event) {
      if (!this.isResizing) return;
      const dx = event.clientX - this.startPos.x;
      const dy = event.clientY - this.startPos.y;
      const dir = this.resizeDir;

      if (dir.includes("right")) this.size.width = this.startSize.width + dx;
      if (dir.includes("left")) {
        this.size.width = this.startSize.width - dx;
        this.position.left = this.startCoords.left + dx;
      }
      if (dir.includes("bottom")) this.size.height = this.startSize.height + dy;
      if (dir.includes("top")) {
        this.size.height = this.startSize.height - dy;
        this.position.top = this.startCoords.top + dy;
      }

      if (this.size.width < 300) this.size.width = 300;
      if (this.size.height < 200) this.size.height = 200;
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);
    },
    zoomIn() {
      this.fitMode = "";
      this.zoomLevel = Math.min(this.zoomLevel + 0.1, 3);
    },
    zoomOut() {
      this.fitMode = "";
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.2);
    },
    fitToWidth() {
      this.fitMode = "width";
      this.updateFitZoom();
    },
    fitToPage() {
      this.fitMode = "page";
      this.updateFitZoom();
    },
    updateFitZoom() {
      this.$nextTick(() => {
        const container = this.$refs.tableContainer;
        const table = container.querySelector("table");
        if (!container || !table) return;
        const scaleX = container.clientWidth / table.scrollWidth;
        const scaleY = container.clientHeight / table.scrollHeight;
        this.zoomLevel =
          this.fitMode === "page" ? Math.min(scaleX, scaleY) : scaleX;
      });
    },
  },
  beforeUnmount() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopResize);
  },
};
</script>

<style scoped>
.excel-viewer.dragging,
.excel-viewer.dragging * {
  user-select: none !important;
}
.excel-viewer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}
.excel-viewer {
  position: absolute;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 0 10px #00000033;
  pointer-events: auto;
  min-height: 60px;
  min-width: auto;
  display: flex;
  flex-direction: column;
  resize: both;
  overflow: hidden;
}
.excel-header {
  background-color: #f0f0f0;
  padding: 5px 10px;
  cursor: move;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ccc;
  height: 30px;
  flex-shrink: 0;
}
.exceltitle {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* allinea a sinistra */
  text-align: left;
}
.excel-controls {
  display: flex;
  gap: 6px;
  overflow: hidden;
  flex-shrink: 0;
}
.excel-header button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 21px;
}
.table-container {
  margin-top: 5px;
  overflow: auto;
  flex-grow: 1;
}
table {
  border-collapse: collapse;
  width: 100%;
}
td {
  border: 1px solid #ccc;
  padding: 8px 12px;
  text-align: left;
  white-space: nowrap;
}
.resize-handle {
  position: absolute;
  z-index: 10;
  background: transparent;
}
.resize-handle:hover {
  background: rgba(0, 0, 0, 0.1);
}
.resize-handle.top {
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  cursor: ns-resize;
}
.resize-handle.bottom {
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  cursor: ns-resize;
}
.resize-handle.left {
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  cursor: ew-resize;
}
.resize-handle.right {
  top: 0;
  bottom: 0;
  right: 0;
  width: 4px;
  cursor: ew-resize;
}
.resize-handle.top-left {
  top: 0;
  left: 0;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}
.resize-handle.top-right {
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}
.resize-handle.bottom-left {
  bottom: 0;
  left: 0;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}
.resize-handle.bottom-right {
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}
.buttonexpand {
  font-size: 21px !important;
  transition: background-color 0.2s ease-in-out;
}
.buttonclose {
  font-size: 16px !important;
  cursor: default !important;
}
.excel-header button:hover {
  background-color: #dadada;
}
</style>
